import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BadgeCheck, Clock, XCircle, Stethoscope, GraduationCap, Pencil, Globe, Lock } from 'lucide-react';
import api from '@/lib/api';

export const STATUS_META = {
  approved: { label: 'Verificado', icon: BadgeCheck, cls: 'bg-green-500/15 text-green-500 border-green-500/30' },
  pending: { label: 'Em análise', icon: Clock, cls: 'bg-amber-500/15 text-amber-500 border-amber-500/30' },
  rejected: { label: 'Recusado', icon: XCircle, cls: 'bg-red-500/15 text-red-500 border-red-500/30' },
};

export const ProfessionalStatusBadge = ({ status }) => {
  const meta = STATUS_META[status];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <Badge variant="outline" className={`gap-1 ${meta.cls}`} data-testid="professional-status-badge">
      <Icon className="h-3.5 w-3.5" /> {meta.label}
    </Badge>
  );
};

const Field = ({ label, value, testid }) => value ? (
  <div><p className="text-xs text-muted-foreground">{label}</p><p className="text-sm font-medium break-words" data-testid={testid}>{value}</p></div>
) : null;

export const ProfessionalCard = ({ userId, editable = false }) => {
  const [card, setCard] = useState(null);

  useEffect(() => {
    if (!userId) return;
    api.get(`/user/${userId}/professional`).then((r) => setCard(r.data)).catch(() => setCard(null));
  }, [userId]);

  if (!card || (card.status === 'none' && !editable)) return null;

  if (card.status === 'none') {
    return (
      <Card className="border-dashed" data-testid="professional-card-empty">
        <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3">
          <Stethoscope className="h-6 w-6 text-purple-500 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold">Perfil Profissional</p>
            <p className="text-sm text-muted-foreground">Você é profissional da saúde ou da recuperação? Cadastre suas credenciais para receber o selo de verificado.</p>
          </div>
          <Button asChild className="min-h-[44px]" data-testid="professional-card-create-btn">
            <Link to="/profile/professional">Criar perfil profissional</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="professional-card">
      <CardContent className="p-4 sm:p-5 space-y-4">
        <div className="flex flex-wrap items-start gap-3">
          <Stethoscope className="h-6 w-6 text-purple-500 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Perfil Profissional</p>
            <p className="text-lg font-bold break-words" data-testid="professional-card-name">{card.professional_name}</p>
          </div>
          <div className="w-full sm:w-auto"><ProfessionalStatusBadge status={card.status} /></div>
        </div>
        {editable && card.status === 'rejected' && card.reject_reason && (
          <p className="text-sm text-red-500 bg-red-500/10 rounded-lg p-3" data-testid="professional-card-reject-reason">
            Motivo: {card.reject_reason}. Ajuste os dados e envie novamente.
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Especialidade" value={card.specialty} testid="professional-card-specialty" />
          <Field label="Instituição" value={card.institution} testid="professional-card-institution" />
          <Field label="Registro profissional" value={card.registry} testid="professional-card-registry" />
        </div>
        {card.short_cv && <p className="text-sm text-muted-foreground whitespace-pre-line" data-testid="professional-card-cv">{card.short_cv}</p>}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {card.status === 'approved' && (
            <Button asChild variant="outline" size="sm" className="min-h-[40px] gap-2" data-testid="professional-card-pareiad-link">
              <Link to="/formacao/parei-ad"><GraduationCap className="h-4 w-4" /> Formação PAREI-AD</Link>
            </Button>
          )}
          {editable && (
            <>
              <span className="text-xs text-muted-foreground flex items-center gap-1" data-testid="professional-card-visibility">
                {card.visibility === 'public' ? <><Globe className="h-3.5 w-3.5" /> Público</> : <><Lock className="h-3.5 w-3.5" /> Privado</>}
              </span>
              <Button asChild size="sm" variant="ghost" className="ml-auto min-h-[40px] gap-2" data-testid="professional-card-edit-btn">
                <Link to="/profile/professional"><Pencil className="h-4 w-4" /> Editar</Link>
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
